import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CircleNotch, Eye, EyeSlash } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import AuthShell from '../components/layout/AuthShell';
import { useAuth } from '../contexts/AuthContext';
import { formatApiErrorDetail } from '../lib/utils';
import {
  PASSWORD_POLICY_HINT,
  passwordErrorMessage,
  validatePasswordParts,
} from '../lib/passwordValidation';

export default function RegisterPage() {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [organizationName, setOrganizationName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const passwordCheck = validatePasswordParts(password);
    if (!passwordCheck.valid) {
      setError(passwordErrorMessage(passwordCheck, t));
      return;
    }
    setLoading(true);

    try {
      await register({
        email,
        password: passwordCheck.password,
        name,
        organization_name: organizationName,
      });
      navigate('/dashboard');
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title={t('auth.registerTitle')}
      description={t('auth.registerDesc')}
      footer={(
        <p className="text-center text-sm text-muted-foreground">
          {t('auth.haveAccount')}{' '}
          <Link
            to="/login"
            className="font-medium text-brand-600 hover:text-brand-700"
            data-testid="login-link"
          >
            {t('auth.signIn')}
          </Link>
        </p>
      )}
    >
      {error && (
        <div
          className="mb-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300"
          data-testid="register-error"
        >
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="space-y-2">
          <Label htmlFor="name">{t('auth.fullName')}</Label>
          <Input
            id="name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="John Doe"
            required
            data-testid="register-name-input"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="email">{t('common.email')}</Label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com"
            required
            data-testid="register-email-input"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="organization">{t('auth.orgName')}</Label>
          <Input
            id="organization"
            type="text"
            value={organizationName}
            onChange={(e) => setOrganizationName(e.target.value)}
            placeholder="Your Company Inc."
            required
            data-testid="register-org-input"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="password">{t('common.password')}</Label>
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t('auth.passwordRequirementsHint', { defaultValue: PASSWORD_POLICY_HINT })}
              required
              minLength={10}
              data-testid="register-password-input"
              className="pr-11"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 rounded-lg p-1 text-muted-foreground hover:text-foreground"
              data-testid="toggle-password-btn"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeSlash className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>
          <p className="text-xs text-muted-foreground">
            {t('auth.passwordRequirementsHint', { defaultValue: PASSWORD_POLICY_HINT })}
          </p>
        </div>

        <Button type="submit" disabled={loading} data-testid="register-submit-btn" className="w-full">
          {loading ? <CircleNotch className="h-5 w-5 animate-spin" /> : t('auth.createAccount')}
        </Button>
      </form>
    </AuthShell>
  );
}
